// financePackagePdf.ts — the branded BOARD PACKET as a real PDF.
//
// This is NOT a screenshot of a dashboard page. It renders the SAME content and
// numbers as the multi-sheet XLSX Finance Package (lib/financePackage.ts) — one
// PDF section per workbook tab — from the same `FinancePackageData` snapshot, so
// the .xlsx and .pdf never drift. jsPDF + autotable are dynamically imported by
// the builder, so this adds no weight to the main bundle until the user clicks.
//
// Sections (mirroring the workbook):
//   Cover / Read Me · Levy Position (3-lens reconciliation + by-object) ·
//   Open Purchase Orders · FY27 Budget (by committee) · FY27 Bridge ·
//   FY27 Initiatives · Non-Profit (QBO) · QBO Metrics · FA.PL.001 Compliance.
//
// Branding: every page gets the DCVFD/Ember logo, org + packet title, the as-of
// date + FY scope, a DCVFD-green accent rule, and a running footer with page
// numbers. The FA.PL.001 compliance section is emphasized per board request.

import {
  computeQboMetrics,
  renderBarChart,
  renderLineChart,
  chartPngDataUrl,
  CHART_ASPECT,
  NET_KPI_HOLD,
  NET_KPI_TREND_NOTE,
  type FinancePackageData,
} from "./financePackage";

// Shared chart palette — the SAME hex the XLSX charts and the dashboard use, so
// the three exports stay visually consistent.
const C_GREEN = "#163832";
const C_GREEN_MID = "#2A7A6C";
const C_AMBER = "#B8860B";

// jsPDF's autotable plugin is called functionally; types come from the package.
import type jsPDFType from "jspdf";
import { fundForFy } from "./fiscal";

// ── brand tokens (RGB tuples for jsPDF) ───────────────────────────────────────
const GREEN: [number, number, number] = [22, 56, 50]; // #163832
const ACCENT: [number, number, number] = [42, 122, 108]; // #2A7A6C
const ROLLUP: [number, number, number] = [234, 245, 234]; // #EAF5EA light green
const ZEBRA: [number, number, number] = [243, 246, 245]; // #F3F6F5
const NOTE_BG: [number, number, number] = [255, 247, 230]; // #FFF7E6 amber
const NOTE_TX: [number, number, number] = [122, 88, 0]; // #7A5800
const RED: [number, number, number] = [178, 58, 46]; // #B23A2E
const MUTED: [number, number, number] = [110, 110, 110];
const INK: [number, number, number] = [40, 40, 40];

const LOGO_PATH = "/brand/ember-light.png";
const PAGE_W = 595.28; // A4 portrait pt
const PAGE_H = 841.89;
const MX = 40; // left/right margin
const CONTENT_W = PAGE_W - MX * 2;

// ── formatters ────────────────────────────────────────────────────────────────
function num(v: unknown): number {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) ? n : 0;
}
function usd(v: unknown): string {
  const n = Math.round(num(v));
  const s = Math.abs(n).toLocaleString("en-US");
  return n < 0 ? `-$${s}` : `$${s}`;
}

// jsPDF's built-in fonts are WinAnsi (Latin-1) only — Unicode symbols (arrows,
// Greek, dingbats, true-minus) render as garbage ("→"→"!'", "Δ"→'"', "⚑"→"&'").
// Map them to ASCII so the board PDF never shows mojibake. Em-dash/middot/curly
// quotes ARE in WinAnsi, so leave them. Applied in every text helper + doc.text.
function S(v: unknown): string {
  return String(v ?? "")
    .replace(/[→⟶➔➜⇒]/g, "->")
    .replace(/[←⟵]/g, "<-")
    .replace(/[↔]/g, "<->")
    .replace(/Δ/g, "chg ")
    .replace(/[⚑⚐⊕✓✔]/g, "*")
    .replace(/[−–]/g, "-") // true-minus + en-dash -> hyphen (middot · is WinAnsi, kept)
    .replace(/[•]/g, "-")
    .replace(/≥/g, ">=")
    .replace(/≤/g, "<=")
    .replace(/…/g, "...");
}

type Doc = jsPDFType & {
  lastAutoTable?: { finalY: number };
};

// ── logo fetch → dataURL (once) ───────────────────────────────────────────────
async function fetchLogoDataUrl(): Promise<string | null> {
  try {
    const res = await fetch(LOGO_PATH);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onloadend = () =>
        resolve(typeof fr.result === "string" ? fr.result : null);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// Fetch a font file and base64-encode it for jsPDF's VFS (client/browser path;
// the Worker passes the bytes via opts instead, so it never bundles the font).
async function fetchFontB64(path: string): Promise<string | null> {
  try {
    const res = await fetch(path);
    if (!res.ok) return null;
    const buf = new Uint8Array(await res.arrayBuffer());
    let bin = "";
    for (let i = 0; i < buf.length; i++) bin += String.fromCharCode(buf[i]);
    return btoa(bin);
  } catch {
    return null;
  }
}

async function renderFinancePackagePdf(
  data: FinancePackageData,
  opts?: {
    logoDataUrl?: string | null;
    aptosNormal?: string | null;
    aptosBold?: string | null;
  },
): Promise<Doc> {
  const [{ jsPDF }, autoTableMod] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  // Resolve the autoTable function robustly. In the browser (vite) the default
  // export is the callable; under the Worker's esbuild/CJS interop it may instead
  // expose applyPlugin() which registers doc.autoTable. Handle both so the PDF
  // builds server-side (TypeError: autoTable is not a function otherwise).
  const atMod = autoTableMod as unknown as {
    default?: unknown;
    applyPlugin?: (j: unknown) => void;
  };
  let autoTable: (doc: unknown, options: unknown) => void;
  if (typeof atMod.default === "function") {
    autoTable = atMod.default as (doc: unknown, options: unknown) => void;
  } else if (typeof (autoTableMod as unknown) === "function") {
    autoTable = autoTableMod as unknown as (
      doc: unknown,
      options: unknown,
    ) => void;
  } else if (typeof atMod.applyPlugin === "function") {
    atMod.applyPlugin(jsPDF);
    autoTable = (doc: unknown, options: unknown) =>
      (doc as { autoTable: (o: unknown) => void }).autoTable(options);
  } else {
    throw new Error("jspdf-autotable: no callable export found");
  }
  const logo =
    opts && "logoDataUrl" in opts ? opts.logoDataUrl : await fetchLogoDataUrl();

  const doc = new jsPDF({ unit: "pt", format: "a4" }) as Doc;
  // Embed Aptos (the DCVFD / M365 brand font) so the board PDF matches the app.
  // Best-effort: fall back to Helvetica if the font assets aren't reachable.
  const aptosN = opts?.aptosNormal ?? (await fetchFontB64("/brand/Aptos.ttf"));
  const aptosB =
    opts?.aptosBold ?? (await fetchFontB64("/brand/Aptos-Bold.ttf"));
  let FONT = "helvetica";
  if (aptosN && aptosB) {
    try {
      doc.addFileToVFS("Aptos.ttf", aptosN);
      doc.addFont("Aptos.ttf", "Aptos", "normal");
      doc.addFileToVFS("Aptos-Bold.ttf", aptosB);
      doc.addFont("Aptos-Bold.ttf", "Aptos", "bold");
      FONT = "Aptos";
    } catch {
      FONT = "helvetica";
    }
  }
  doc.setFont(FONT, "normal");
  // Global catch-all: sanitize every string rendered (incl. autoTable cells, which
  // call doc.text internally) so no Unicode glyph mojibakes in the board PDF.
  const _docText = doc.text.bind(doc);
  (doc as { text: unknown }).text = (t: unknown, ...rest: unknown[]) =>
    (_docText as (t: unknown, ...r: unknown[]) => unknown)(
      Array.isArray(t) ? t.map(S) : S(t),
      ...rest,
    );

  // ── page chrome ─────────────────────────────────────────────────────────────
  const header = (subtitle: string): number => {
    // faint band
    doc.setFillColor(246, 250, 248);
    doc.rect(0, 0, PAGE_W, 54, "F");
    if (logo) {
      try {
        doc.addImage(logo, "PNG", MX, 12, 110, 33);
      } catch {
        /* logo optional */
      }
    }
    doc.setTextColor(...GREEN);
    doc.setFont(FONT, "bold");
    doc.setFontSize(13);
    doc.text("Dale City Volunteer Fire Department", MX + 122, 24);
    doc.setFontSize(9.5);
    doc.setTextColor(...INK);
    doc.text(`Board Finance Packet — ${subtitle}`, MX + 122, 38);
    // right-aligned meta
    doc.setFont(FONT, "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    const asOf = data.asOf ? `as of ${data.asOf}` : "as-of date pending sync";
    doc.text(
      [
        `${data.curFy}${data.curFyIsPrior ? " (prior yr)" : ""} position · ${data.nextFy} budget`,
        `fund ${fundForFy(data.curFy)} · ${asOf}`,
      ],
      PAGE_W - MX,
      22,
      { align: "right" },
    );
    // green accent rule
    doc.setFillColor(...GREEN);
    doc.rect(0, 54, PAGE_W, 2.5, "F");
    return 74;
  };

  const sectionTitle = (y: number, text: string): number => {
    doc.setFillColor(...GREEN);
    doc.rect(MX, y, CONTENT_W, 18, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont(FONT, "bold");
    doc.setFontSize(10);
    doc.text(text, MX + 6, y + 12.5);
    return y + 26;
  };

  const callout = (y: number, text: string): number => {
    doc.setFont(FONT, "normal");
    doc.setFontSize(8.5);
    const lines = doc.splitTextToSize(S(text), CONTENT_W - 16);
    const h = lines.length * 11 + 12;
    doc.setFillColor(...NOTE_BG);
    doc.setDrawColor(...NOTE_TX);
    doc.setLineWidth(0.5);
    doc.roundedRect(MX, y, CONTENT_W, h, 3, 3, "FD");
    doc.setTextColor(...NOTE_TX);
    doc.text(lines, MX + 8, y + 13);
    return y + h + 10;
  };

  const para = (y: number, text: string, size = 9): number => {
    doc.setFont(FONT, "normal");
    doc.setFontSize(size);
    doc.setTextColor(...INK);
    const lines = doc.splitTextToSize(S(text), CONTENT_W);
    doc.text(lines, MX, y);
    return y + lines.length * (size + 3) + 4;
  };

  // page registry so we can stamp footers with total count at the end
  const pageSubtitles: string[] = [];
  const startPage = (subtitle: string): number => {
    if (pageSubtitles.length > 0) doc.addPage();
    pageSubtitles.push(subtitle);
    return header(subtitle);
  };

  // Place a canvas-rendered chart image (same renderer as the XLSX) into the PDF,
  // sized to the content width and breaking to a new page if it won't fit. No-op
  // when the chart couldn't render (e.g. no canvas server-side) — tables remain.
  const chartImg = (
    y: number,
    buf: ArrayBuffer | null,
    pageSubtitle: string,
  ): number => {
    const url = chartPngDataUrl(buf);
    if (!url) return y;
    const w = CONTENT_W * 0.66;
    const h = w / CHART_ASPECT;
    if (y + h > PAGE_H - 40) {
      doc.addPage();
      pageSubtitles.push(pageSubtitle);
      y = header(pageSubtitle);
    }
    try {
      doc.addImage(url, "PNG", MX, y, w, h);
    } catch {
      return y;
    }
    return y + h + 12;
  };

  // autotable wrapper with brand styling
  const table = (
    y: number,
    head: string[],
    body: (string | number)[][],
    opts: Record<string, unknown> = {},
  ): number => {
    autoTable(doc, {
      startY: y,
      head: [head.map(S)],
      body: body.map((row) =>
        row.map((c) => (typeof c === "number" ? c : S(c))),
      ),
      margin: { left: MX, right: MX },
      styles: {
        font: FONT,
        fontSize: 7.8,
        cellPadding: 3,
        textColor: INK,
        lineColor: [226, 232, 230],
        lineWidth: 0.3,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: GREEN,
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 7.8,
      },
      alternateRowStyles: { fillColor: ZEBRA },
      ...opts,
    });
    return (doc.lastAutoTable?.finalY ?? y) + 14;
  };

  // ── 1. COVER / READ ME ────────────────────────────────────────────────────
  {
    let y = startPage("Cover / Read Me");
    y = sectionTitle(y, "DCVFD Finance Package");
    y = para(
      y,
      `This packet is the single board-facing finance source: the County levy position (fund ${fundForFy(data.curFy)}), the ` +
        `${data.nextFy} operating budget build, the non-profit (QuickBooks) operating book, and the FA.PL.001 ` +
        "monthly-review compliance checklist. Every figure is pulled live from Oracle/Mobius and QuickBooks — " +
        "nothing here is hand-keyed. It is generated identically to the .xlsx package, one section per tab.",
    );
    const fa = data.available?.all_object_available;
    const cash = data.nonprofit?.cash_available;
    y = callout(
      y,
      `Headline: levy Funds Available (budgetary control) = ${
        fa != null ? usd(fa) : "syncing"
      }  ·  non-profit cash available = ${cash != null ? usd(cash) : "syncing"}. ` +
        "Always quote Funds Available (not gross budget-remaining) to the Board — see the Levy Position section.",
    );
    y = sectionTitle(y, "What each section contains");
    table(
      y,
      ["Section", "Contains", "How to read it"],
      [
        [
          "Levy Position",
          "3-lens Funds-Available reconciliation + by-object budget/committed/obligated/actual",
          "Lead with the headline Funds Available; the waterfall shows why the object report reads higher.",
        ],
        [
          "Open Purchase Orders",
          "Open-encumbrance register (PO, supplier, remaining, FY)",
          "These commitments draw down the levy as they invoice.",
        ],
        [
          `${data.nextFy} Budget`,
          "Recurring operating budget by committee with FY24-26 actuals",
          "Light-green rows are committee subtotals; lines are suppliers/objects.",
        ],
        [
          `${data.nextFy} Bridge`,
          "Base → operating need build-up incl. approved adds",
          "Shows what's IN the operating levy vs funded outside it.",
        ],
        [
          `${data.nextFy} Initiatives`,
          "County initiative forms 27.001–27.007 + status",
          "Approved operating adds are additive via the Bridge, not in base.",
        ],
        [
          "Non-Profit (QBO)",
          "501(c)(4) book: banks, income by source, expenses",
          "Pass-through (PWC) is separated from own-funded activity.",
        ],
        [
          "QBO Metrics",
          "Derived BOD metrics (cash, runway, composition)",
          "n/a where QBO doesn't expose the input — never invented.",
        ],
        [
          "FA.PL.001 Compliance",
          "Monthly-review checklist with status",
          "Work this every meeting — the standing monthly cadence.",
        ],
      ],
      {
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 95 },
          2: { textColor: MUTED },
        },
      },
    );
  }

  // ── 2. LEVY POSITION ──────────────────────────────────────────────────────
  {
    let y = startPage("Levy Position");
    const av = data.available;
    const gross =
      av?.expenditure_budget_remaining != null
        ? num(av.expenditure_budget_remaining)
        : null;
    const bcea =
      av?.expenditure_only_available != null
        ? num(av.expenditure_only_available)
        : null;
    // Headline = single-source-of-truth snapshot (ties to Oracle), recon fallback.
    const headline =
      av?.snapshot_available != null
        ? num(av.snapshot_available)
        : av?.all_object_available != null
          ? num(av.all_object_available)
          : null;
    const gapMaterial =
      gross != null &&
      bcea != null &&
      gross - bcea > Math.max(1000, Math.abs(headline ?? 0) * 0.02);

    y = sectionTitle(
      y,
      gapMaterial
        ? "Funds Available — 3-lens reconciliation"
        : "Funds Available — reconciliation",
    );
    const revShort = bcea != null && headline != null ? bcea - headline : null;
    const lensRows: (string | number)[][] = gapMaterial
      ? [
          [
            "1 · Budget remaining (gross) — overstates spendable",
            gross != null ? usd(gross) : "—",
          ],
          [
            "2 · BCEA expenditure-only available (County spending side)",
            bcea != null ? usd(bcea) : "—",
          ],
          [
            "3 · Funds Available (budgetary control) — real spendable",
            headline != null ? usd(headline) : "—",
          ],
        ]
      : [
          [
            "1 · Expenditure budget available (Budget − spent − open POs)",
            bcea != null ? usd(bcea) : "—",
          ],
          [
            "2 · less budgeted-but-uncollected revenue (netted in)",
            revShort != null ? usd(-revShort) : "—",
          ],
          [
            "3 · Funds Available (budgetary control) — real spendable",
            headline != null ? usd(headline) : "—",
          ],
        ];
    y = table(y, ["Lens", "Amount"], lensRows, {
      columnStyles: {
        1: { halign: "right", cellWidth: 110, fontStyle: "bold" },
      },
      didParseCell: (d: {
        section: string;
        row: { index: number };
        cell: { styles: Record<string, unknown> };
      }) => {
        if (d.section === "body" && d.row.index === 2) {
          d.cell.styles.textColor = ACCENT;
          d.cell.styles.fontStyle = "bold";
        }
      },
    });
    if (headline != null) {
      const tail = gapMaterial
        ? `${usd(headline)} is what we can actually still spend; ${
            gross != null ? usd(gross) : "the gross figure"
          } on the by-object report overstates it (it hasn't netted anticipated/capital or uncollected revenue).`
        : `${usd(headline)} is the controlling spendable figure; the expenditure budget alone reads ${
            bcea != null ? usd(bcea) : "higher"
          } before netting ${revShort != null ? usd(revShort) : "the revenue shortfall"} of budgeted-but-uncollected recovery revenue.`;
      y = callout(y, `${tail} Always quote Funds Available to the Board.`);
    }

    // chart: levy budget vs spent vs available (same renderer as the XLSX)
    const lbudget = data.summary ? num(data.summary.expenditure?.budget) : null;
    const lspent = data.summary
      ? num(data.summary.expenditure?.consumed)
      : null;
    if (lbudget != null && lspent != null && headline != null) {
      y = chartImg(
        y,
        renderBarChart(
          "Levy — budget vs spent vs available",
          ["Budget", "Spent (consumed)", "Funds Available"],
          [{ color: C_GREEN_MID, values: [lbudget, lspent, headline] }],
        ),
        "Levy Position",
      );
    }

    // by-object table
    y = sectionTitle(y, "By object — budgetary control (expenditure)");
    const objs = (data.byObject ?? []).slice(0, 26);
    if (objs.length === 0) {
      y = para(y, "(syncing — by-object detail not yet loaded)", 8.5);
    } else {
      table(
        y,
        [
          "Object",
          "Description",
          "Budget",
          "Committed",
          "Obligated",
          "Actual",
          "Remaining",
        ],
        objs.map((o) => [
          o.object_segment,
          o.object_desc ?? "",
          usd(o.budget_amount),
          usd(o.commitment_amount),
          usd(o.obligation_amount),
          usd(o.actual_amount),
          usd(o.funds_available),
        ]),
        {
          columnStyles: {
            0: { cellWidth: 42 },
            1: { cellWidth: 120 },
            2: { halign: "right" },
            3: { halign: "right" },
            4: { halign: "right" },
            5: { halign: "right" },
            6: { halign: "right", fontStyle: "bold" },
          },
          didParseCell: (d: {
            column: { index: number };
            cell: { raw: unknown; styles: Record<string, unknown> };
          }) => {
            if (d.column.index === 6 && String(d.cell.raw).startsWith("-")) {
              d.cell.styles.textColor = RED;
            }
          },
        },
      );
    }
  }

  // ── 3. OPEN PURCHASE ORDERS ───────────────────────────────────────────────
  {
    let y = startPage("Open Purchase Orders");
    y = sectionTitle(y, "Open-encumbrance register");
    const pos = data.openPos ?? [];
    const total = pos.reduce((s, p) => s + num(p.remaining), 0);
    // Per-appropriation split so this register self-reconciles with the Levy
    // Position section: operating fund == the by-object obligation sum; the
    // fund-2101 carryover rides its own swept appropriation.
    const opTotal = pos
      .filter((p) => (p.fund ?? "2119") !== "2101")
      .reduce((s, p) => s + num(p.remaining), 0);
    const carryPos = pos.filter((p) => p.fund === "2101");
    const carryTotal = carryPos.reduce((s, p) => s + num(p.remaining), 0);
    y = para(
      y,
      `${pos.length} open purchase orders, ${usd(total)} remaining encumbrance — these draw down the levy as they invoice.` +
        (carryPos.length > 0
          ? ` Split: ${usd(opTotal)} operating (fund 2119 — ties the Levy Position obligation total) + ${usd(carryTotal)} FY26 carryover (${carryPos.length} POs, fund 2101 — their own swept appropriation, not this year's budget).`
          : ""),
    );
    if (pos.length === 0) {
      y = para(y, "(syncing — open-PO register not yet loaded)", 8.5);
    } else {
      table(
        y,
        ["PO #", "Supplier", "Object", "FY", "Remaining"],
        pos
          .slice()
          .sort((a, b) => num(b.remaining) - num(a.remaining))
          .slice(0, 34)
          .map((p) => [
            p.po_number,
            p.supplier ?? p.description ?? "",
            p.object_segment ?? "",
            String(p.fy_established ?? ""),
            usd(p.remaining),
          ]),
        {
          columnStyles: {
            0: { cellWidth: 70 },
            1: { cellWidth: 200 },
            3: { halign: "center", cellWidth: 36 },
            4: { halign: "right", fontStyle: "bold" },
          },
        },
      );
    }
  }

  // ── 4. FY27 BUDGET (06-JUL v2 revision; the old v1 sheet was retired) ──────
  {
    const v2 = data.fy27V2;
    let y = startPage(`${data.nextFy} Budget`);
    y = sectionTitle(
      y,
      `${data.nextFy} v2 — 06-JUL revision (Committee > Category > Expense type)`,
    );
    // Cent-exact for the carryover / ALL-IN figures (they tie to the cent).
    const usd2 = (n: number) =>
      `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    y = callout(
      y,
      v2
        ? `Three labeled layers per committee — never blended: Recurring v2 ${usd(v2.grand.recurring)} ` +
            `(v1 15-JUN ${usd(v2.grand.v1_recurring)}; net change ${v2.grand.delta >= 0 ? "+" : ""}${usd(v2.grand.delta)}) ` +
            `+ Funded projects ${usd(v2.grand.projects_funded)} ` +
            `+ ${v2.labels.po_carryover} ${usd2(v2.grand.po_carryover)} (${v2.grand.po_carryover_count} POs) ` +
            `= GRAND ALL-IN ${usd2(v2.grand.all_in)}. ` +
            "The board reviews the v1-to-v2 delta + specific line changes — not a re-vote. Every FY actual ties to the GL; suppliers/OCAs are backing detail. A * marks lines pending director confirmation. Arrears exposure is informational and excluded from every sum."
        : "(syncing — FY27 v2 not yet loaded)",
    );
    if (v2) {
      const rows: (string | number)[][] = [];
      const rollupIdx: number[] = [];
      const layerIdx: number[] = []; // layer-subtotal rows (bold, no fill)
      for (const cmte of v2.committees) {
        rollupIdx.push(rows.length);
        rows.push([
          cmte.committee,
          "",
          usd(cmte.fy26_actual),
          usd(cmte.recurring),
          `${cmte.delta_v1 >= 0 ? "+" : ""}${usd(cmte.delta_v1)}`,
        ]);
        for (const cat of cmte.categories) {
          rows.push([`  ${cat.category}`, "", "", "", ""]);
          for (const l of cat.lines) {
            rows.push([
              "",
              l.expense_type + (l.needs_director_confirm ? " *" : ""),
              usd(num(l.fy26_actual)),
              usd(num(l.fy27_v2)),
              "",
            ]);
          }
        }
        // The three labeled layers, compact (G-1/G-2/G-3).
        layerIdx.push(rows.length);
        rows.push([
          "  Funded projects (one-time — additive)",
          cmte.projects_funded
            .map((p) => `${p.ref ? p.ref + " " : ""}${p.name}`)
            .join("; ") || "none",
          "",
          usd(cmte.projects_funded_total),
          "",
        ]);
        if (cmte.projects_in_recurring.length > 0)
          rows.push([
            "",
            cmte.projects_in_recurring
              .map((p) => `${p.name} ${usd(p.amount)}`)
              .join("; ") + " — in recurring above, not re-added",
            "",
            "",
            "",
          ]);
        if (cmte.projects_not_funded.length > 0)
          rows.push([
            "",
            "Not funded: " +
              cmte.projects_not_funded
                .map((p) => `${p.name} ${usd(p.amount)} — ${p.status}`)
                .join("; "),
            "",
            "",
            "",
          ]);
        layerIdx.push(rows.length);
        rows.push([
          "  FY26→FY27 open-PO carryover (confirmed at FY close)",
          `${cmte.po_carryover.count} open POs`,
          "",
          usd2(cmte.po_carryover.total),
          "",
        ]);
        if (cmte.arrears_note) rows.push(["", cmte.arrears_note, "", "", ""]);
        layerIdx.push(rows.length);
        rows.push([
          "  ALL-IN (recurring + projects + carryover)",
          "",
          "",
          usd2(cmte.all_in),
          "",
        ]);
      }
      y = table(
        y,
        [
          "Committee / Category",
          "Expense type",
          "FY26 actual",
          `${data.nextFy} v2`,
          "Net chg",
        ],
        rows,
        {
          columnStyles: {
            0: { cellWidth: 120, fontStyle: "bold" },
            2: { halign: "right" },
            3: { halign: "right" },
            4: { halign: "right", cellWidth: 56 },
          },
          didParseCell: (d: {
            section: string;
            row: { index: number };
            cell: { styles: Record<string, unknown> };
          }) => {
            if (d.section === "body" && rollupIdx.includes(d.row.index)) {
              d.cell.styles.fillColor = ROLLUP;
              d.cell.styles.fontStyle = "bold";
            } else if (d.section === "body" && layerIdx.includes(d.row.index)) {
              d.cell.styles.fontStyle = "bold";
            }
          },
        },
      );
      // Projects schedule (one-time / capital) — where the capital items live.
      if (v2.projects && v2.projects.length) {
        if (y > PAGE_H - 140) y = startPage(`${data.nextFy} Budget`);
        const order = [
          "Fund balance",
          "Operating levy",
          "Insurance",
          "Rejected",
          "Deferred",
        ];
        const rank = (s: string) => {
          const i = order.indexOf(s);
          return i === -1 ? order.length : i;
        };
        const sources = [
          ...new Set(
            v2.projects.map((p) => p.funding_source || "(unassigned)"),
          ),
        ].sort((a, b) => rank(a) - rank(b));
        const prows: (string | number)[][] = [];
        const pgroup: number[] = [];
        for (const src of sources) {
          const rs = v2.projects.filter(
            (p) => (p.funding_source || "(unassigned)") === src,
          );
          const tot = rs.reduce((s, p) => s + num(p.amount), 0);
          pgroup.push(prows.length);
          prows.push([src, "", usd(tot)]);
          for (const p of rs)
            prows.push([
              `  ${p.ref ? p.ref + " " : ""}${p.name}${p.affects_operating ? " (in recurring)" : ""}`,
              p.status || "",
              usd(num(p.amount)),
            ]);
        }
        y = sectionTitle(
          y,
          "Projects schedule (one-time / capital — by funding source)",
        );
        table(y, ["Funding / project", "Status", "Amount"], prows, {
          columnStyles: {
            0: { cellWidth: 300, fontStyle: "bold" },
            2: { halign: "right" },
          },
          didParseCell: (d: {
            section: string;
            row: { index: number };
            cell: { styles: Record<string, unknown> };
          }) => {
            if (d.section === "body" && pgroup.includes(d.row.index)) {
              d.cell.styles.fillColor = ROLLUP;
              d.cell.styles.fontStyle = "bold";
            }
          },
        });
      }
    }
  }

  // FY27 Bridge section ELIMINATED per RJ 6/15 — the base→operating-need walk
  // confused the board. Approved operating adds + carryover are part of the
  // FY27 Budget conversation instead.

  // ── 6. FY27 INITIATIVES ───────────────────────────────────────────────────
  {
    let y = startPage(`${data.nextFy} Initiatives`);
    y = sectionTitle(y, "County initiatives (Phase I forms 27.001–27.007)");
    const projects = data.projects ?? [];
    if (projects.length === 0) {
      y = para(y, "(no initiatives loaded)", 8.5);
    } else {
      table(
        y,
        ["Initiative", "Committee", "Estimate", "Status", "Board"],
        projects.map((p) => [
          p.name,
          p.committee ?? "",
          usd(p.cost_estimate),
          p.status ?? "",
          p.board_approved ? "Approved" : "—",
        ]),
        {
          columnStyles: {
            0: { cellWidth: 180 },
            2: { halign: "right" },
            4: { halign: "center" },
          },
        },
      );
    }
  }

  // ── 7. NON-PROFIT (QBO) ───────────────────────────────────────────────────
  {
    let y = startPage("Non-Profit (QBO)");
    const np = data.nonprofit;
    y = sectionTitle(y, "DCVFD 501(c)(4) operating book (QuickBooks / Truist)");
    if (!np?.connected) {
      y = callout(
        y,
        "QuickBooks not connected — non-profit figures will populate once the QBO sync is live.",
      );
    } else {
      y = callout(
        y,
        `Cash available ${usd(np.cash_available)} (total ${usd(np.cash_total)} − payroll set-aside ${usd(
          np.cash_restricted,
        )}). The PWC reimbursement pass-through is separated from own-funded activity below.`,
      );
      const banks = np.bank_accounts ?? [];
      if (banks.length) {
        y = sectionTitle(y, "Bank accounts");
        y = table(
          y,
          ["Account", "Number", "Balance", "Restricted"],
          banks.map((a) => [
            a.name ?? "",
            a.acct_num ?? "",
            usd(a.balance),
            a.restricted ? "Yes" : "",
          ]),
          { columnStyles: { 2: { halign: "right" }, 3: { halign: "center" } } },
        );
      }
      const inc = data.npIncome;
      if (inc?.rows?.length) {
        y = sectionTitle(y, "Income by source — pass-through vs own-funded");
        y = table(
          y,
          ["Source", "Type", "Amount"],
          inc.rows
            .slice(0, 18)
            .map((rrow) => [
              rrow.source ?? "",
              rrow.is_pwc ? "PWC pass-through" : "Own-funded",
              usd(rrow.amount),
            ]),
          {
            columnStyles: { 1: { textColor: MUTED }, 2: { halign: "right" } },
            foot: [["Total", "", usd(inc.total)]],
            footStyles: {
              fillColor: ROLLUP,
              textColor: INK,
              fontStyle: "bold",
            },
          },
        );
      }
      const exp = data.npExpenses;
      if (exp?.rows?.length) {
        y = sectionTitle(y, "Expenses by category");
        table(
          y,
          ["Category", "Amount"],
          exp.rows
            .slice(0, 18)
            .map((rrow) => [rrow.category ?? "", usd(rrow.amount)]),
          {
            columnStyles: { 1: { halign: "right", cellWidth: 120 } },
            foot: [["Total", usd(exp.total)]],
            footStyles: {
              fillColor: ROLLUP,
              textColor: INK,
              fontStyle: "bold",
            },
          },
        );
      }
    }
  }

  // ── 8. QBO METRICS ────────────────────────────────────────────────────────
  {
    let y = startPage("QBO Metrics");
    y = sectionTitle(y, "Derived BOD metrics (501(c)(4) operating book)");
    const metrics = computeQboMetrics(
      data.nonprofit ?? null,
      data.npTimeseries ?? null,
    );
    y = table(
      y,
      ["Metric", "Value", "How it's computed"],
      metrics.map((m) => [m.label, m.value, m.formula]),
      {
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 150 },
          1: { halign: "right", cellWidth: 90 },
          2: { textColor: MUTED, fontSize: 7 },
        },
      },
    );
    // chart: income vs expense vs net (trailing 24 mo) — same renderer as the XLSX
    const months = (data.npTimeseries?.months ?? []).slice(-24);
    if (months.length > 1) {
      chartImg(
        y,
        renderLineChart(
          "Income vs expense — trailing 24 months",
          months.map((m) => m.label),
          [
            {
              label: "Income",
              color: C_GREEN_MID,
              values: months.map((m) => num(m.income)),
            },
            {
              label: "Expense",
              color: C_AMBER,
              values: months.map((m) => num(m.expense)),
            },
            // The Net series carries the 55601 contra-credit that forces the Net
            // operating / Net income ROWS to be withheld in the XLSX. Plotting it
            // here unqualified would put the withheld figure in front of the Board
            // as a chart in the SAME email that withholds it as a number — the
            // Jul-2026 spike is the distortion, not performance. Dropped while the
            // hold is on; Income and Expense still plot, as they do elsewhere.
            ...(NET_KPI_HOLD
              ? []
              : [
                  {
                    label: "Net",
                    color: C_GREEN,
                    values: months.map((m) => num(m.net)),
                  },
                ]),
          ],
        ),
        "QBO Metrics",
      );
      if (NET_KPI_HOLD) {
        y = callout(y, NET_KPI_TREND_NOTE);
      }
    }
  }

  // ── 9. FA.PL.001 COMPLIANCE (emphasized) ──────────────────────────────────
  {
    let y = startPage("FA.PL.001 Compliance");
    y = sectionTitle(y, "Financial Policy (FA.PL.001) — monthly review duties");
    y = callout(
      y,
      "MONTHLY REVIEW CADENCE — FA.PL.001 requires the Board to review the financials EVERY MONTH: monthly " +
        "financials reviewed (§12.1), bank reconciliations current and independently reviewed (§10), purchase " +
        "approvals within the §5.5 thresholds, and budget-vs-actual reviewed. This is the standing monthly " +
        "checklist — work it at each meeting.",
    );
    const qbo = !!data.nonprofit?.connected;
    // Same v2-sourced committee-approval fix as financePackage.ts (XLSX) —
    // dash_fy27_committee (via fy27Build) was abandoned at the 2026-07-06 v2
    // restructure and never advances past "0/8 draft"; the live approval
    // signal now lives on dash_fy27_v2_committee (via fy27V2).
    const v2Committees = (data.fy27V2?.committees ?? []).filter(
      (c) => !c.is_unassigned,
    );
    const approved =
      v2Committees.length > 0
        ? v2Committees.filter((c) => c.status === "approved").length
        : null;
    const totalC = v2Committees.length > 0 ? v2Committees.length : null;
    const fy27OnTrack =
      approved != null && totalC != null && totalC > 0
        ? approved >= totalC
        : null;
    const statusLabel = (s: boolean | null) =>
      s === true ? "Met" : s === false ? "Action needed" : "Confirm (verbal)";
    const items: [string, boolean | null, string][] = [
      [
        "Monthly financials reviewed (§12.1)",
        null,
        "Board confirms the monthly financials were presented and reviewed.",
      ],
      [
        "Bank reconciliations current & independently reviewed (§10)",
        qbo ? null : false,
        qbo
          ? "Confirm in QuickBooks that all accounts are reconciled through last month."
          : "Pending QuickBooks connection — reconciliations not yet auto-verifiable.",
      ],
      [
        "Purchase approvals within FA.PL.001 §5.5 thresholds",
        null,
        "Board confirms approvals were within each band's threshold.",
      ],
      [
        "Budget vs actual reviewed",
        true,
        "The Levy Position + by-object sections satisfy the monthly budget-vs-actual review.",
      ],
      [
        `${data.nextFy} budget — formal Board adoption`,
        fy27OnTrack,
        approved != null && totalC != null
          ? `v2 revision built 06-JUL; formal adoption pending — on the next meeting agenda (see ${data.nextFy} Budget section).`
          : "FY27 build not loaded.",
      ],
      [
        "Unbudgeted board-approved items tied to a motion",
        null,
        "Board confirms any unbudgeted approved item ties back to a recorded motion.",
      ],
    ];
    y = table(
      y,
      ["Monthly review requirement", "Status", "Basis / how to confirm"],
      items.map(([label, s, basis]) => [label, statusLabel(s), basis]),
      {
        columnStyles: {
          0: { cellWidth: 175 },
          1: { halign: "center", cellWidth: 80, fontStyle: "bold" },
          2: { textColor: MUTED, fontSize: 7.5 },
        },
        didParseCell: (d: {
          section: string;
          column: { index: number };
          row: { index: number };
          cell: { styles: Record<string, unknown> };
        }) => {
          if (d.section === "body" && d.column.index === 1) {
            const s = items[d.row.index][1];
            d.cell.styles.textColor =
              s === true ? ACCENT : s === false ? RED : NOTE_TX;
          }
        },
      },
    );
    para(
      y,
      'A "Confirm (verbal)" status means there is no automated data source yet — the Board confirms it at the ' +
        "meeting; it wires up as QuickBooks and the reconciliation feed come online. No item here fabricates a value.",
      7.5,
    );
  }

  // ── footers (page X of N + confidential) on every page ─────────────────────
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setDrawColor(...GREEN);
    doc.setLineWidth(0.5);
    doc.line(MX, PAGE_H - 28, PAGE_W - MX, PAGE_H - 28);
    doc.setFont(FONT, "normal");
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(
      "DCVFD Board Finance Packet — confidential, for board use",
      MX,
      PAGE_H - 18,
    );
    doc.text(
      `${pageSubtitles[i - 1] ?? ""}  ·  page ${i} of ${total}`,
      PAGE_W - MX,
      PAGE_H - 18,
      {
        align: "right",
      },
    );
  }

  return doc;
}

/** Build the board packet PDF and trigger a browser download. */
export async function downloadFinancePackagePdf(
  data: FinancePackageData,
): Promise<void> {
  const doc = await renderFinancePackagePdf(data);
  const stamp = (data.asOf ?? "current").replace(/[^0-9A-Za-z-]/g, "");
  doc.save(`Board-Finance-Packet-${stamp}.pdf`);
}

/** Build the board packet PDF and return it as a Blob (no download) — used by
 *  the Worker weekly-email job. Pass opts.logoDataUrl since the Worker has no
 *  DOM FileReader; charts skip server-side (no canvas) but all tables render. */
export async function buildFinancePackagePdfBlob(
  data: FinancePackageData,
  opts?: {
    logoDataUrl?: string | null;
    aptosNormal?: string | null;
    aptosBold?: string | null;
  },
): Promise<Blob> {
  const doc = await renderFinancePackagePdf(data, opts);
  return new Blob([doc.output("arraybuffer") as ArrayBuffer], {
    type: "application/pdf",
  });
}
